import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { computeBriefingNav } from "@/lib/navcalc";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Play, FileDown, Loader2, Navigation, Fuel, Scale, Ruler,
  ClipboardCheck, ChevronDown, CheckCircle2, MessageSquare, Map, Globe,
} from "lucide-react";

import NavLogTab from "@/components/briefing/NavLogTab";
import NavMapTab from "@/components/briefing/NavMapTab";
import FuelTab from "@/components/briefing/FuelTab";
import WeightBalanceTab from "@/components/briefing/WeightBalanceTab";
import LimitationsTab from "@/components/briefing/LimitationsTab";
import ChecklistTab from "@/components/briefing/ChecklistTab";
import PresentationMode from "@/components/briefing/PresentationMode";
import PrintView from "@/components/briefing/PrintView";
import ShareDialog from "@/components/briefing/ShareDialog";
import CommentsPanel from "@/components/briefing/CommentsPanel";
import { CHECKLIST_POINTS } from "@/components/briefing/checklistData";
import RouteEditDialog from "@/components/briefing/RouteEditDialog";
import { mergeRouteIntoBriefing } from "@/lib/routeSync";
import { buildPdfSpec } from "@/lib/pdfSpec";
import { buildNavlogSpec } from "@/lib/navlogSpec";

const TABS = [
  { id: "navlog", label: "Log de nav", icon: Navigation },
  { id: "carte", label: "Carte de Nav", icon: Map },
  { id: "carburant", label: "Carburant", icon: Fuel },
  { id: "centrage", label: "Masse & Centrage", icon: Scale },
  { id: "limitations", label: "Limitations", icon: Ruler },
  { id: "checklist", label: "Checklist 11 pts", icon: ClipboardCheck },
  { id: "comments", label: "Échanges", icon: MessageSquare },
];

export default function BriefingWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [aircraft, setAircraft] = useState(null);
  const [tab, setTab] = useState("navlog");
  const [saving, setSaving] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [printMode, setPrintMode] = useState("full");
  const [comments, setComments] = useState([]);
  const [resources, setResources] = useState({ customSites: [], siteSubs: {}, siteCreds: {} });
  const [route, setRoute] = useState(null);
  const [routeOpen, setRouteOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const routeTimer = useRef(null);

  useEffect(() => {
    api.get("/settings/resources").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  const loadComments = useCallback(async () => {
    try {
      setComments((await api.get(`/briefings/${id}/comments`)).data);
    } catch {
      /* ignore */
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      const b = (await api.get(`/briefings/${id}`)).data;
      if (b.aircraftId) {
        try {
          setAircraft((await api.get(`/aircraft/${b.aircraftId}`)).data);
        } catch {
          setAircraft(null);
        }
      }
      // La route est la source de vérité de la géométrie : on la ré-applique à l'ouverture.
      if (b.routeId) {
        try {
          const r = (await api.get(`/routes/${b.routeId}`)).data;
          setRoute(r);
          setBriefing(mergeRouteIntoBriefing(b, r));
          return;
        } catch {
          /* route supprimée : on garde les legs existants */
        }
      }
      setBriefing(b);
    })();
    loadComments();
  }, [id, loadComments]);

  const postComment = async (text) => {
    await api.post(`/briefings/${id}/comments`, { text });
    await loadComments();
    toast.success("Commentaire ajouté");
  };

  const nav = useMemo(
    () => computeBriefingNav(briefing || {}, aircraft || {}),
    [briefing?.legs, briefing?.returnLegs, briefing?.roundTrip, aircraft]
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.put(`/briefings/${id}`, briefing);
      toast.success("Briefing enregistré");
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  }, [id, briefing]);

  const onRouteSaved = (r) => {
    setRoute(r);
    setBriefing((b) => mergeRouteIntoBriefing(b, r));
  };

  // Édition alt/Rv/décl depuis le log de nav → répercutée sur la route (enregistrement différé).
  const onRouteLegChange = (i, key, v, listKey = "legs") => {
    setRoute((r) => {
      if (!r?.[listKey]?.[i]) return r;
      const legs = [...r[listKey]];
      legs[i] = { ...legs[i], [key]: v };
      const next = { ...r, [listKey]: legs };
      clearTimeout(routeTimer.current);
      routeTimer.current = setTimeout(() => {
        api.put(`/routes/${r.id}`, next).catch(() => toast.error("Route non mise à jour"));
      }, 700);
      return next;
    });
  };

  const exportFile = async ({ path, spec, type, filename, label, done }) => {
    setExporting(true);
    const t = toast.loading(`Génération ${label}…`);
    try {
      const { data } = await api.post(path, spec, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(done || `${label[0].toUpperCase()}${label.slice(1)} téléchargé`, { id: t });
    } catch (e) {
      let detail = "";
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* réponse non JSON */ }
      toast.error(detail || `Échec de la génération ${label}`, { id: t });
    } finally {
      setExporting(false);
    }
  };
  const safeName = (briefing?.name || "briefing").replace(/[^\w-]+/g, "_");

  const downloadPdf = () => {
    const briefingX = { ...briefing, customSites: resources.customSites, siteSubs: resources.siteSubs, siteCreds: resources.siteCreds };
    exportFile({ path: `/briefings/${id}/export-pdf`, spec: buildPdfSpec({ briefing, briefingX, aircraft, nav }),
      type: "application/pdf", filename: `Dossier_VFR_${safeName}.pdf`, label: "dossier PDF" });
  };

  const downloadVac = (codes) =>
    exportFile({ path: `/briefings/${id}/export-vac`, spec: { codes }, type: "application/pdf", filename: `Cartes_VAC_${safeName}.pdf`, label: "cartes VAC", done: "Cartes VAC téléchargées" });

  const downloadNavlog = () =>
    exportFile({ path: `/briefings/${id}/export-navlog`, spec: buildNavlogSpec(briefing, aircraft, nav),
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", filename: `Log_de_nav_${safeName}.xlsx`, label: "log de nav Excel" });

  const doPrint = (mode) => {
    setPrintMode(mode);
    setTimeout(() => window.print(), 120);
  };

  if (!briefing) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const doneCount = CHECKLIST_POINTS.filter((p) => briefing.checklist?.[p.id]?.done).length;
  const missingAircraft = !aircraft;
  // Global reference resources merged in (read-only) so sites/links resolve everywhere.
  const briefingX = { ...briefing, customSites: resources.customSites, siteSubs: resources.siteSubs, siteCreds: resources.siteCreds };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="no-print flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button onClick={() => navigate("/briefings")} className="mb-1 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
            <ArrowLeft className="h-4 w-4" /> Briefings
          </button>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">{briefing.name}</h1>
          <div className="mt-1 flex items-center gap-3 font-mono-data text-sm text-cyan-300">
            <span>{briefing.departure || "?"} → {briefing.arrival || "?"}</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-400">{aircraft?.registration || "Avion supprimé"}</span>
            <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> {doneCount}/11</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setPresenting(true)} variant="outline" data-testid="presentation-button"
            className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-cyan-300">
            <Play className="mr-1.5 h-4 w-4" /> Présentation
          </Button>
          <ShareDialog briefing={briefing} setBriefing={setBriefing} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-pdf-button"
                className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-cyan-300">
                <FileDown className="mr-1.5 h-4 w-4" /> Export PDF <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border-slate-700 bg-[#0D1527] text-slate-100">
              <DropdownMenuItem onClick={downloadPdf} disabled={exporting} data-testid="export-full">Dossier complet (fichier PDF A4)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doPrint("condensed")} data-testid="export-condensed">Version condensée (kneeboard)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={save} disabled={saving} data-testid="save-briefing-button"
            className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Enregistrer</>}
          </Button>
        </div>
      </div>

      {missingAircraft && (
        <div className="no-print rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          L'avion associé est introuvable (supprimé ?). Les calculs de nav, carburant et centrage seront incomplets.
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="no-print">
        <TabsList className="flex h-auto flex-wrap justify-start gap-2 rounded-xl border border-slate-800 bg-slate-950/60 p-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}
                className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-slate-400 data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-300 data-[state=active]:shadow-none">
                <Icon className="h-4 w-4" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="navlog" className="mt-5">
          <NavLogTab briefing={briefing} setBriefing={setBriefing} aircraft={aircraft} nav={nav}
            route={route} onEditRoute={() => setRouteOpen(true)} onRouteLegChange={onRouteLegChange}
            onExportExcel={downloadNavlog} exporting={exporting} briefingId={id} />
        </TabsContent>
        <TabsContent value="carte" className="mt-5">
          <NavMapTab briefing={briefingX} setBriefing={setBriefing} briefingId={id} onExportVac={downloadVac} exporting={exporting} />
        </TabsContent>
        <TabsContent value="carburant" className="mt-5">
          <FuelTab briefing={briefing} setBriefing={setBriefing} aircraft={aircraft} nav={nav} />
        </TabsContent>
        <TabsContent value="centrage" className="mt-5">
          <WeightBalanceTab briefing={briefing} setBriefing={setBriefing} aircraft={aircraft} nav={nav} />
        </TabsContent>
        <TabsContent value="limitations" className="mt-5">
          <LimitationsTab briefing={briefing} setBriefing={setBriefing} aircraft={aircraft} />
        </TabsContent>
        <TabsContent value="checklist" className="mt-5">
          <ChecklistTab briefing={briefingX} setBriefing={setBriefing} onGoTab={setTab} briefingId={id}
            aircraftId={briefing.aircraftId} pilotId={briefing.pilotId} aircraft={aircraft} nav={nav} />
        </TabsContent>
        <TabsContent value="comments" className="mt-5">
          <CommentsPanel comments={comments} onPost={postComment} guest={false} />
        </TabsContent>
      </Tabs>

      {presenting && (
        <PresentationMode briefing={briefingX} nav={nav} aircraft={aircraft} briefingId={id} onClose={() => { setPresenting(false); setTab("checklist"); }} />
      )}

      {route && (
        <RouteEditDialog open={routeOpen} onOpenChange={setRouteOpen} route={route} onSaved={onRouteSaved} />
      )}

      <PrintView briefing={briefing} aircraft={aircraft} nav={nav} mode={printMode} />
    </div>
  );
}
